import type { LocationQuery, LocationQueryRaw } from 'vue-router';
import { TECH_EXPLORATION_DETAILS_PATH } from '@/routers/techExplorationRoutes';
import type { TechExplorationReviewCommentTargetType } from '@/types';
import { routeQueryToString } from '@/utils/routeParams';
import {
  REVIEW_COMMENT_TARGET_TYPE,
  REVIEW_COMMENT_TARGET_TYPES,
} from '@/utils/techExplorations/constants';

type TechExplorationComparisonTargetType = Extract<
  TechExplorationReviewCommentTargetType,
  | typeof REVIEW_COMMENT_TARGET_TYPE.COMPARISON_SOLUTION
  | typeof REVIEW_COMMENT_TARGET_TYPE.COMPARISON_RESULT
>;

export type TechExplorationAnchorTarget = {
  targetType: TechExplorationReviewCommentTargetType;
  targetId: number;
};

// ハッシュ(#)アンカーはメール配信時に %23 にエンコードされハッシュルーティング配下で壊れるため、アンカーはクエリで渡す
export const TECH_EXPLORATION_ANCHOR_QUERY_KEY = 'anchor';

export const buildTechExplorationAnchorId = (
  target: TechExplorationAnchorTarget,
): string => `${target.targetType}-${target.targetId}`;

export const getTechExplorationAnchorQuery = (
  targetType: TechExplorationReviewCommentTargetType,
  targetId: number,
): LocationQueryRaw => ({
  [TECH_EXPLORATION_ANCHOR_QUERY_KEY]: buildTechExplorationAnchorId({
    targetType,
    targetId,
  }),
});

export const getTechExplorationAnchorIdFromQuery = (
  query: LocationQuery,
): string | null =>
  routeQueryToString(query[TECH_EXPLORATION_ANCHOR_QUERY_KEY]);

export const parseTechExplorationAnchorId = (
  anchorId: string | null,
): TechExplorationAnchorTarget | null => {
  if (!anchorId) return null;

  const separatorIndex = anchorId.lastIndexOf('-');
  if (separatorIndex <= 0) return null;

  const rawTargetType = anchorId.slice(0, separatorIndex);
  if (
    !REVIEW_COMMENT_TARGET_TYPES.includes(
      rawTargetType as TechExplorationReviewCommentTargetType,
    )
  ) {
    return null;
  }

  const rawTargetId = anchorId.slice(separatorIndex + 1);
  if (!/^\d+$/.test(rawTargetId)) return null;

  const targetId = Number(rawTargetId);
  if (!Number.isInteger(targetId)) return null;

  return {
    targetType: rawTargetType as TechExplorationReviewCommentTargetType,
    targetId,
  };
};

export const isTechExplorationComparisonTargetType = (
  targetType: TechExplorationReviewCommentTargetType,
): targetType is TechExplorationComparisonTargetType =>
  targetType === REVIEW_COMMENT_TARGET_TYPE.COMPARISON_SOLUTION ||
  targetType === REVIEW_COMMENT_TARGET_TYPE.COMPARISON_RESULT;

export const getTechExplorationCommentThreadRouteName = (
  targetType: TechExplorationReviewCommentTargetType,
): 'techExplorationDetailsView' | 'techExplorationComparison' =>
  isTechExplorationComparisonTargetType(targetType)
    ? 'techExplorationComparison'
    : 'techExplorationDetailsView';

export const getTechExplorationCommentThreadPath = (
  uuid: string,
  targetType: TechExplorationReviewCommentTargetType,
): string =>
  isTechExplorationComparisonTargetType(targetType)
    ? `${TECH_EXPLORATION_DETAILS_PATH.replace(':uuid', uuid)}/comparison`
    : TECH_EXPLORATION_DETAILS_PATH.replace(':uuid', uuid);
